<!--
 * @Author: Vben
 * @Description: Multi-language switching component
-->
<template>
  <Dropdown
    placement="bottom"
    :trigger="['hover']"
    :dropMenuList="localeList"
    :selectedKeys="selectedKeys"
    @menu-event="handleMenuEvent"
    overlayClassName="app-locale-picker-overlay"
  >
    <span class="app-locale-picker-trigger cursor-pointer flex items-center">
      <Icon icon="language|svg" />
      <span v-if="showText" class="ml-1">{{ getLocaleText }}</span>
    </span>
  </Dropdown>
</template>
<script lang="ts" setup>
  import type { LocaleType } from '/#/config';
  import type { DropMenu } from '/@/components/Dropdown';
  import { ref, watchEffect, unref, computed } from 'vue';
  import { Dropdown } from '/@/components/Dropdown';
  import { Icon } from '/@/components/Icon';
  import { useLocale } from '/@/locales/useLocale';
  import { localeList } from '/@/settings/localeSetting';

  const props = defineProps({
    /**
     * Whether to display text
     */
    showText: { type: Boolean, default: true },
    /**
     * Whether to refresh the interface when changing
     */
    reload: { type: Boolean },
  });

  const selectedKeys = ref<string[]>([]);

  const { changeLocale, getLocale } = useLocale();

  const getLocaleText = computed(() => {
    const key = selectedKeys.value[0];
    if (!key) {
      return '';
    }
    return localeList.find((item) => item.event === key)?.text;
  });

  watchEffect(() => {
    selectedKeys.value = [unref(getLocale)];
  });

  async function toggleLocale(lang: LocaleType | string) {
    // 关闭中英文切换的环境不提供切换入口，这里是防御性拦截，
    // 避免该组件日后被复用时打出机房后端不存在的接口。
    if (!__I18N_ENABLED__) {
      return;
    }
    await changeLocale(lang as LocaleType);
    selectedKeys.value = [lang as string];
    // 同步后端语言偏好（en -> en-US，zh_CN -> zh-CN）
    try {
      const { updateCurrentLanguage } = await import('/@/api/nurse/AccountService');
      await updateCurrentLanguage({ locale: lang === 'en' ? 'en-US' : 'zh-CN' });
    } catch (e) {
      console.error('同步语言偏好失败:', e);
    }
    props.reload && location.reload();
  }

  function handleMenuEvent(menu: DropMenu) {
    if (unref(getLocale) === menu.event) {
      return;
    }
    toggleLocale(menu.event as string);
  }
</script>

<style lang="less">
  .app-locale-picker-overlay {
    .ant-dropdown-menu-item {
      min-width: 160px;
    }
  }
</style>

<style lang="less" scoped>
  .app-locale-picker-trigger {
    min-width: 56px;
    padding: 0 14px;
    transition: background-color 0.2s;
    justify-content: center;
  }
</style>
